import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Target, BookOpen, EyeOff, Sparkles } from "lucide-react";
import { startCheckout } from "@/lib/checkout";
import { useSeo } from "@/lib/useSeo";

const LOGO = "/assets/logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const PILLARS = [
  {
    icon: EyeOff,
    title: "Faceless by design",
    desc: "Everything we publish is built for creators who want to grow and earn without ever showing their face or filming themselves.",
  },
  {
    icon: BookOpen,
    title: "Actionable, not fluffy",
    desc: "Step-by-step ebooks, real hooks, and ready-to-post reels — practical resources you can use the same day you download them.",
  },
  {
    icon: Target,
    title: "Built to monetize",
    desc: "Master Resell Rights included so you can rebrand our products, sell them as your own, and keep 100% of the profit.",
  },
];

function AboutHeader() {
  return (
    <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-black/70 border-b border-white/5">
      <div className="max-w-5xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="about-home-link">
          <img src={LOGO} alt="Social Media Combo logo" className="w-8 h-8 object-contain" />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Social Media Combo</span>
            <span className="font-serif-display text-base tracking-[0.25em] font-semibold">
              THE FACELESS BUNDLE<span className="text-[#D4AF37]">.</span>
            </span>
          </div>
        </Link>
        <button
          onClick={startCheckout}
          data-testid="about-header-cta"
          className="hidden sm:inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-black transition-colors"
        >
          Get Access — $27
        </button>
      </div>
    </header>
  );
}

export default function About() {
  useSeo({
    title: "About Social Media Combo — Faceless Social Media Marketing Ebooks",
    description:
      "Social Media Combo is a digital publisher of faceless social-media marketing ebooks, guides, and ready-to-post reels that help creators grow and monetize anonymous brands on Instagram, TikTok and YouTube.",
    path: "/about",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <AboutHeader />

      {/* Hero / definition */}
      <section className="relative pt-36 pb-20 grain" data-testid="about-hero">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#D4AF37]/8 blur-[140px] rounded-full pointer-events-none" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10">
          <motion.p initial="hidden" animate="show" variants={fadeUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37]">
            About us
          </motion.p>
          <motion.h1
            initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]"
          >
            What is <span className="gold-shimmer">Social Media Combo</span>?
          </motion.h1>
          <motion.p
            initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-8 text-lg sm:text-xl text-zinc-300 leading-relaxed"
          >
            Social Media Combo is a <strong className="text-white">digital publisher of faceless social-media marketing
            ebooks, guides, and ready-to-post reels</strong> that help creators build, grow, and monetize anonymous brands
            on Instagram, TikTok &amp; YouTube — without ever showing their face or filming themselves.
          </motion.p>
          <motion.p
            initial="hidden" animate="show" custom={3} variants={fadeUp}
            className="mt-5 text-base text-zinc-400 leading-relaxed"
          >
            Our flagship product is <Link to="/" className="text-[#D4AF37] hover:underline">The Faceless Marketing Bundle</Link> —
            11 in-depth ebooks plus free bonus <Link to="/bonuses" className="text-[#D4AF37] hover:underline">hooks and reels</Link> —
            a complete, beginner-friendly system for launching a profitable faceless brand from scratch.
          </motion.p>
        </div>
      </section>

      {/* Mission */}
      <section className="relative py-16 md:py-20 bg-[#080808] border-y border-[#D4AF37]/15" data-testid="about-mission">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Our mission</span>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl font-semibold leading-tight">
              Make faceless income accessible to anyone
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Not everyone wants to be an influencer. We believe you shouldn't have to show your face, build a personal
              brand, or spend years learning video editing to earn online. Social Media Combo packages the exact
              frameworks, hooks, and content that top faceless creators use into affordable, ready-to-use digital products —
              so anyone can start a modern, anonymous social media business with nothing but a phone and a plan.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="relative py-20 md:py-24" data-testid="about-pillars">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i} variants={fadeUp}
                data-testid={`about-pillar-${i}`}
                className="border border-zinc-800 bg-[#121212] p-7 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-zinc-800 bg-black">
                  <p.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="mt-5 font-serif-display text-xl font-semibold">{p.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 bg-[#080808] border-t border-[#D4AF37]/20 grain overflow-hidden" data-testid="about-cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/8 blur-[130px] rounded-full pointer-events-none" />
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold leading-tight">
            Start your faceless brand today
          </h2>
          <p className="mt-4 text-zinc-400">
            Get all 11 ebooks + 3 free bonuses for a one-time <span className="text-white font-semibold">$27</span>.
          </p>
          <button onClick={startCheckout} data-testid="about-cta-button" className="btn-gold mt-8 inline-flex items-center gap-3 px-10 py-5 text-base sm:text-lg">
            Get The Faceless Bundle <ArrowRight className="w-5 h-5" />
          </button>
          <div className="mt-10">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors" data-testid="about-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to store
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-600">
          <span className="font-serif-display tracking-widest">SOCIAL MEDIA COMBO.</span>
          <div className="flex items-center gap-6">
            <Link to="/blog" className="hover:text-[#D4AF37] transition-colors">Blog</Link>
            <Link to="/bonuses" className="hover:text-[#D4AF37] transition-colors">Bonus</Link>
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
